import { ContractDetail, LineItems, Payment } from './../../models/contract-model';
import { Component, OnInit, Input, OnDestroy, OnChanges, LOCALE_ID, Output, EventEmitter, HostListener, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateFormatType } from '../../enums/date-format-type.enum';
import { ConfigurationService } from '../../services/configuration.service';
import { Consumed } from '../../enums/consumed-payment-verify';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { BehaviorSubject, catchError, filter, map, Observable, of, Subscription, take, tap } from 'rxjs';
import { ContractPdfResponse } from '../../models/pdf-response';
import { VersionToggleService } from '../../services/version-toggle';
import { AppFacadeService } from '../../services/app-facade.service';
import { ContractService } from '../../services/contract.service';
import { GapiLicense } from '../../enums/gapi-license';
import { AppEventService } from '../../services/app-event.service';
import { OriginActionName } from '@por/shared/ui/cross-app';
import { AuthKeys } from '../../enums/local-storage-keys.enum';
import { ComponentsToLoad } from '../../enums/components-to-load.enum';
import { PathRoutingService } from '../../services/path-routing.service';
import { ConsumerPortalConfig } from '../../models/consumer-portal-config';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ModalService } from '../../services/modal.service';
import { RequestActionsToLoad } from '../../enums/request-actions.enum';
import { RequestActionService } from '../../services/request-action.service';
import { PorTransactionService } from '../../por-transaction.service';
import { ContractDetailView } from '../../enums/contract-detail-view.enum';
import { AppMediatorService } from '../../services/app-mediator.service';
import { LineItemClassification } from '../../enums/LineItemClassification.enum';
import { ContractStatusEnum } from '../../enums/contract-status.enum';
import { ContractStatus } from '../../types/contract.type';
import { LinkField } from '../../enums/link-field';
import { DialogType } from '../../enums/dialog-type.enum';
import { ItemOutService } from '../../services/item-out.service';
import { AVOID_LINE_ITEM_NAMES, LOAD_MORE_DROP_DOWN_DEFAULT_VALUE } from '../../constants/default.const';
import { PaymentMinMaxBoundary } from '../../payment/models/payment-data.model';
import { PaymentMethodEnums } from '../../enums/payment-method.enum';
import { getQuoteStepStatus, QuoteStepName, QuoteStepStatus } from '../../services/quote-step.service';
import { EsignStateEnum } from '../../enums/esign-state.enum';

@Component({
    standalone: false,
    selector: 'por-contract-detail',
    templateUrl: './contract-detail.component.html',
    styleUrls: ['./contract-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractDetailComponent implements OnInit, OnDestroy, OnChanges {
    private readonly translateService = inject(TranslateService);
    readonly featureToggleService = inject(FeatureToggleService);
    private readonly configService = inject(ConfigurationService);
    readonly versionToggle = inject(VersionToggleService);
    readonly appFacadeService = inject(AppFacadeService);
    readonly contractService = inject(ContractService);
    private readonly appEventService = inject(AppEventService);
    private readonly breakpointObserver = inject(BreakpointObserver);
    private readonly modal = inject(ModalService);
    private readonly requestActionService = inject(RequestActionService);
    private readonly porTransactionService = inject(PorTransactionService);
    private readonly appMediatorService = inject(AppMediatorService);
    readonly itemOutService = inject(ItemOutService);
    private readonly cdr = inject(ChangeDetectorRef);

    @Input() contractDetail!: ContractDetail[] | null;
    @Input() customerId!: string;
    @Input() openForPayment = false;
    @Output() readonly reloadContract = new EventEmitter();
    @Output() readonly reloadContractList = new EventEmitter();
    @Output() readonly closePanel = new EventEmitter<boolean>(false);
    @Output() readonly returnToLogin = new EventEmitter<boolean>(false);
    @Output() readonly loginButtonVisibilityChange = new EventEmitter<boolean>();
    dateFormat: DateFormatType;
    isDialogEnabled = false;
    isPaymentEnabled = false;
    isPending = false;
    // AC1: Syrinx account-type status ('cash' vs 'account') for the payment accordion gate.
    customerStatus?: string;
    // AC2: configured minimum (paymentBoundary.minAmount) the cumulative paid is compared against.
    paymentMinimum: number | null = null;
    // Payment boundaries (min/max per method) surfaced in the accordion when neither the payment-history
    // table nor the "Make a payment" button renders, so the accordion still shows the amounts.
    paymentBoundaries: PaymentMinMaxBoundary[] = [];
    organizationId!: string;
    @Input() uiUrl: string | undefined;
    pdfDownloadAvailable$ = new BehaviorSubject<boolean>(this.featureToggleService.isAvailable('pdfDownloadFeature'));
    subscription: Subscription[] = [];
    displayedRowCount = LOAD_MORE_DROP_DOWN_DEFAULT_VALUE;
    pdfDownloadUrl$ = new BehaviorSubject<ContractPdfResponse | null>(null);
    singleProductPageOpened = false;
    subscriptions: Subscription[] = [];
    isMobile = false;
    isContractCloneModal = false;
    isProductDetailAvailable = true;
    selectedLineItem!: LineItems;
    private readonly activeTemplate = new BehaviorSubject(ContractDetailView.DefaultView);
    activeTemplate$ = this.activeTemplate.asObservable();
    dialogType: DialogType = DialogType.confirm;
    contractId!: string;
    urlsByDocumentType: { [key: string]: string } = {};
    esignMsg = '';
    isSticky = false;
    sticky!: number;
    paymentRequired = this.featureToggleService.isAvailable('paymentRequired');
    eSignRequired = this.featureToggleService.isAvailable('eSignRequired');
    secureUrlLoginRequired = this.featureToggleService.isAvailable('secureUrlLoginRequired');
    autoConvertQuotes = this.featureToggleService.isAvailable('autoConvertQuotes');
    // RMS-native reservations were never quotes in CP, so the tracker only follows a CP auto-convert.
    private convertedContractIds: string[] = [];
    quoteStepStatus: QuoteStepStatus = getQuoteStepStatus({ paymentRequired: false, eSignRequired: false, paymentMinimum: null });
    // Which contract's Id the current paymentMinimum was fetched for. WHY: this component is reused
    // (not recreated) across contract switches on the Contracts tab, so paymentMinimum from a
    // previously-viewed contract would otherwise linger and be read as the new contract's payment
    // status. HOW: ngOnChanges compares this against the incoming contractDetail's Id and re-fetches
    // the boundary whenever they differ, instead of relying on the one-time ngOnInit fetch.
    private paymentBoundaryContractId: string | undefined;
    isPaymentProcessorAvailable$: Observable<boolean> = this.appFacadeService.isPaymentProcessorsAvailable().pipe(
        map(processors => {
            if (!processors) {
                return false;
            }
            return (
                Array.isArray(processors) &&
                processors.some(processor => {
                    return processor.RemoteDepotId === this.contractDetail?.[0].DepotId;
                })
            );
        })
    );

    constructor() {
        this.dateFormat = DateFormatType.StandardDateTime;
    }

    checkPaymentEnabled() {
        const amount: number = this.contractDetail?.[0]?.AmountDue ? this.contractDetail?.[0]?.AmountDue : 0;
        const consumed: Consumed | undefined = this.contractDetail?.[0]?.Payment?.[0]?.Consumed;
        // Allow payment if the amount due is greater than 0 and Consumer status is Posted or Error
        if (consumed !== Consumed.Waiting && consumed !== Consumed.Imported && amount > 0) {
            this.isPaymentEnabled = true;
        }
        if (this.contractDetail?.[0].Payment && this.contractDetail?.[0].Payment?.some(payment => payment.Consumed === Consumed.Waiting || payment.Consumed === Consumed.Imported)) {
            this.isPending = true;
        }
        /*
         *@setting pdfEnabled button  if details response has pdf properties
         */
        if (this.contractDetail?.[0]?.Pdf?.SecureURL) {
            this.pdfDownloadAvailable$.next(true);
        }

        if (this.configService.config?.organizationId) {
            this.organizationId = this.configService.config?.organizationId;
        }

        // Check if the capturePaymentFeature is not available globally
        const isCapturePaymentFeatureAvailable: boolean = this.featureToggleService.isAvailable('capturePaymentFeature');

        // Get the contract status, considering both possible properties
        const contractStatus: string | undefined = this.contractDetail?.[0]?.Status ?? this.contractDetail?.[0].ContractStatus;

        // Check if the canCapturePayment sub-feature is available for the current contract status, ignoring the global feature check
        const canCapturePayment: boolean = this.featureToggleService.isAvailable('capturePaymentFeature', {
            subFeature: 'canCapturePayment',
            state: contractStatus as string,
            ignoreFeatureCheck: true
        });
        // Enable payment if the global feature is not available, but the sub-feature is available for the current contract status
        if (!isCapturePaymentFeatureAvailable) {
            if (canCapturePayment) {
                this.isPaymentEnabled = true;
            } else {
                this.isPaymentEnabled = false;
            }
        }
        // Check if contract staus is quotes and quotesPaymentAbility is true then show Payment Button else hide it.
        if (this.contractDetail?.[0]?.ContractStatus === ContractStatusEnum.Quote && !this.featureToggleService.isAvailable('quotesPaymentAbility')) {
            this.isPaymentEnabled = false; // Hide the button
        } else {
            this.isPaymentEnabled = true; // Show the button
        }

        // Always hide payment button for Voided contracts
        if (this.contractDetail?.[0]?.ContractStatus === ContractStatusEnum.Voided) {
            this.isPaymentEnabled = false;
        }

        // This check should work only for some RMS systems, that's why We have feature toggle for this
        if (this.featureToggleService.features.checkPaymentLicense && !this.versionToggle.isLicenseActive(GapiLicense.AcquiringCCProccessing)) {
            this.isPaymentEnabled = false;
        }

        // Consumer has no permission to see the payment button, then hide it
        if (!this.featureToggleService.isAvailable('Account.SeePricing')) {
            this.isPaymentEnabled = false;
        }

        // Consumer has no permission to see the amounts, then hide it
        if (!this.featureToggleService.isAvailable('canSeeAmounts')) {
            this.isPaymentEnabled = false;
        }

        // ADO-114246: a credit (amount due < 0) is never payable — keep this last so no earlier
        // branch (e.g. the non-quote else above) can re-enable the button for a negative balance.
        if (amount < 0) {
            this.isPaymentEnabled = false;
        }
    }

    // AC1: only for Syrinx Cash customers (hidden for Syrinx Account).
    get isSyrinxAccountCustomer(): boolean {
        return this.featureToggleService.isSyrinx() && (this.customerStatus ?? '').toLowerCase() === 'account';
    }

    // ADO-114246: a credit (amount due < 0) is never payable.
    get isCreditContract(): boolean {
        return (this.contractDetail?.[0]?.AmountDue ?? 0) < 0;
    }

    // Accordion visibility: View Payments permission (AC4) + Payment Required toggle (AC7)
    get isPaymentAccordionVisible(): boolean {
        // ADO-114246: on a credit the panel has nothing payable — show it only when the payment-history
        // table itself would render (View Payments + at least one payment), never an empty/boundary-only panel.
        if (this.isCreditContract) {
            const historyVisible = this.featureToggleService.isAvailable('Account.PaymentHistory') && (this.contractDetail?.[0]?.Payment?.length ?? 0) > 0;
            return historyVisible && !this.isSyrinxAccountCustomer;
        }
        if (!this.isPaymentEnabled && !this.featureToggleService.isAvailable('Account.PaymentHistory')) {
            return false;
        }
        return !this.isSyrinxAccountCustomer;
    }

    // The eSign accordion shows whenever a contract is loaded, for every state including None;
    // getEsignStepStatus/startEsign handle each state. Hidden only when no contract is present.
    get isEsignAccordionVisible(): boolean {
        return this.hasContractDetail();
    }

    getEsignStepStatus(detail: ContractDetail | undefined): 'complete' | 'incomplete' | 'pending' {
        if (detail?.ESignState === EsignStateEnum.Signed) {
            return 'complete';
        }
        return detail?.ESignState === EsignStateEnum.Requested ? 'pending' : 'incomplete';
    }

    // requestEsignUpdate('Create') is the single eSign entry point: it mints the envelope and sets the provider
    // redirect URL. A repeat Create within 60s with the same RedirectUrl returns the same link.
    startEsign(): void {
        const detail = this.contractDetail?.[0];
        const contractId = detail?.Id;
        if (!contractId) {
            return;
        }
        // Already signed: por-base-button's [disabled] only greys the control (ApxButtonComponent toggles
        // CSS, not the native disabled), so a re-click still reaches this handler.
        if (this.getEsignStepStatus(detail) === 'complete') {
            return;
        }
        // Non-consumer QuickLink visitors must log in first when the org requires it; the dedicated marker
        // lets the post-login resume re-run Create.
        if (this.secureUrlLoginRequired && this.versionToggle.isQLButNotConsumerQL()) {
            this.persistEsignResumeMarker(contractId);
            this.appFacadeService.updateLocation(ComponentsToLoad.Logout);
            this.returnToLogin.emit(true);
            return;
        }
        this.requestEsignAndRedirect(contractId);
    }

    private persistEsignResumeMarker(contractId: string): void {
        this.appMediatorService.localStorageService.setEsignResumeMarker({ customerId: this.customerId, contractId });
    }

    // The esign-request route is exempt from the interceptor's 502/504 dialog, so failures are surfaced here.
    private requestEsignAndRedirect(contractId: string): void {
        let activeTab: ComponentsToLoad | undefined;
        this.appFacadeService
            .getActiveTab()
            .pipe(take(1))
            .subscribe(tab => (activeTab = tab));
        const redirectUrl = PathRoutingService.buildEsignRedirectUrl(contractId, activeTab);

        this.esignMsg = '';
        this.appFacadeService.setLoading(true);
        this.subscriptions.push(
            this.contractService.requestEsignUpdate(this.customerId, contractId, redirectUrl, 'Create').subscribe({
                next: response => {
                    this.appFacadeService.setLoading(false);
                    if (response?.esignUrl && PathRoutingService.isSafeEsignUrl(response.esignUrl)) {
                        window.location.href = response.esignUrl;
                    } else {
                        this.redirectToFallbackEsignUrlOr('ESignRequestFailed');
                    }
                },
                error: error => {
                    this.appFacadeService.setLoading(false);
                    // A 504 may mean Create still completed server-side; the stored EsignUrl carries no
                    // RedirectUrl (no esignReturn), so a signer sent there never returns. Surface the
                    // timeout retry instead of the fallback. Other failures (e.g. orgs whose backend does
                    // not implement esign-request) fall back to the provider link on the contract.
                    if (error?.status === 504) {
                        this.showEsignMessage('ESignRequestRetry');
                        return;
                    }
                    this.redirectToFallbackEsignUrlOr('ESignRequestFailed');
                }
            })
        );
    }

    // On an eSign Create failure, fall back to the provider link already on the contract
    // detail before surfacing an error. Backend-supplied, so gate it through isSafeEsignUrl
    // (javascript:/data: guard — see component CLAUDE.md eSign section).
    private redirectToFallbackEsignUrlOr(messageKey: string): void {
        const fallbackUrl = this.contractDetail?.[0]?.EsignUrl;
        if (fallbackUrl && PathRoutingService.isSafeEsignUrl(fallbackUrl)) {
            window.location.href = fallbackUrl;
            return;
        }
        this.showEsignMessage(messageKey);
    }

    private showEsignMessage(messageKey: string): void {
        this.esignMsg = this.translateService.instant(messageKey);
        this.cdr.markForCheck();
    }

    // Translated payment-method name for a boundary row.
    getPaymentMethodName(method: PaymentMethodEnums): string {
        return this.appMediatorService.porPayService.getPaymentMethod(method);
    }

    // Shared by the initial ngOnInit fetch and the post-payment poll. ADO-104813 Issue #2: an
    // empty/unresolved boundary is UNKNOWN, not "met" — falls back to Infinity so the badge reads
    // 'incomplete', never 'complete'. ADO-115939: OnPush + imperative writes require markForCheck().
    private applyPaymentBoundaries(boundaries: PaymentMinMaxBoundary[] | null): void {
        this.paymentBoundaries = boundaries ?? [];
        this.paymentMinimum = boundaries?.length ? Math.min(...boundaries.map(boundary => boundary.minAmount)) : Infinity;
        // ADO-104815: the step tracker reads quoteStepStatus, which is derived from paymentMinimum - recompute it
        // here so every path that lands a boundary (initial load AND the post-payment poll) updates the tracker.
        this.recomputeStepStatus();
        this.cdr.markForCheck();
    }

    // After a payment, the boundary may not yet reflect the mutation — retry until the minimum
    // changes from its pre-payment value (or the retry budget is exhausted; see PorPayService).
    // paymentMinimum is reset to null so getPaymentStepStatus() reads 'pending' (spinner) meanwhile.
    private refetchPaymentBoundaryAfterPayment(): void {
        if (!this.contractDetail) {
            return;
        }
        const previousMinimum = this.paymentMinimum;
        this.paymentMinimum = null;
        this.cdr.markForCheck();
        this.subscriptions.push(
            this.appMediatorService.porPayService
                .getMinMaxFromPaymentBoundaryUntilChanged(this.customerId, this.contractDetail?.[0].Id, previousMinimum)
                .pipe(catchError(() => of(null)))
                .subscribe(boundaries => this.applyPaymentBoundaries(boundaries))
        );
    }

    // Show the payment-boundary summary only when the accordion would otherwise be empty: neither the
    // payment-history table (View Payments permission + at least one payment) nor the "Make a payment"
    // button (payment processor available + payment enabled) is rendered, and boundaries are available.
    isPaymentBoundarySummaryVisible(detail: ContractDetail | undefined, isProcessorAvailable: boolean | null): boolean {
        // ADO-114246: never surface the boundary-summary fallback on a credit (amount due < 0).
        if ((detail?.AmountDue ?? 0) < 0) {
            return false;
        }
        const historyVisible = this.featureToggleService.isAvailable('Account.PaymentHistory') && (detail?.Payment?.length ?? 0) > 0;
        const buttonVisible = !!isProcessorAvailable && this.isPaymentEnabled;
        return !historyVisible && !buttonVisible;
    }

    // AC2: local completion badge — derived entirely from the payment boundary minimum (minAmount > 0 => incomplete, <= 0 => complete).
    getPaymentStepStatus(detail: ContractDetail | undefined): 'complete' | 'incomplete' | 'pending' {
        if (detail === undefined || this.paymentMinimum === null) {
            return 'pending';
        }
        return this.paymentMinimum > 0 ? 'incomplete' : 'complete';
    }

    // Infinity is the "unresolved boundary" sentinel from applyPaymentBoundaries — never a real
    // amount to show. Intl formats it as '∞' rather than throwing, so it must be filtered here.
    isPaymentMinimumDisplayable(): boolean {
        return this.paymentMinimum !== null && Number.isFinite(this.paymentMinimum);
    }

    // Clears the marker once the lookup resolves so a refresh does not re-trigger it; keeps it on
    // failure so a reload retries.
    private refreshEsignOnReturn(): void {
        const detail = this.contractDetail?.[0];
        if (!detail?.Id || detail.Id !== PathRoutingService.esignReturnContractId) {
            return;
        }
        this.subscriptions.push(
            this.contractService.getEsignState(this.customerId, detail.Id).subscribe({
                next: response => {
                    // Copy rather than mutate: the input can be frozen NgRx state.
                    // eslint-disable-next-line @typescript-eslint/naming-convention -- DB model casing
                    this.contractDetail = [{ ...detail, ESignState: response.EsignState }, ...(this.contractDetail?.slice(1) ?? [])];
                    if (response.EsignState === EsignStateEnum.Signed) {
                        // Spread over the store copy, not the init-time input: the reducer replaces the whole detail and
                        // would drop LineItems/Payment merged in since load. The dispatch re-runs the auto-convert recheck.
                        this.appFacadeService
                            .getContractDetail()
                            .pipe(take(1))
                            // eslint-disable-next-line @typescript-eslint/naming-convention -- DB model casing
                            .subscribe(current => this.appFacadeService.setContractDetail({ ...(current?.Id === detail.Id ? current : detail), ESignState: response.EsignState }));
                        this.esignMsg = this.translateService.instant('ESignComplete');
                    } else if (response.EsignState === EsignStateEnum.Rejected) {
                        this.esignMsg = this.translateService.instant('ESignRejected');
                    } else {
                        this.esignMsg = this.translateService.instant('ESignNotCompleted');
                    }
                    PathRoutingService.clearEsignReturn();
                    this.cdr.markForCheck();
                },
                error: () => {
                    // Keep the marker so a reload retries the status check; refreshEsignOnReturn only runs on
                    // (re)init, so this cannot loop within a session.
                    this.esignMsg = this.translateService.instant('ESignStatusUnavailable');
                    this.cdr.markForCheck();
                }
            })
        );
    }

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }
        this.checkPaymentEnabled();
        // ADO-114494: checkPaymentEnabled reads the fail-closed Account.SeePricing gate once, but the
        // permission map loads asynchronously (worst on the Contract-QuickLink journey). Re-run it when
        // the map resolves so the payment button reflects the real capability instead of the empty-map value.
        this.subscriptions.push(
            this.appFacadeService
                .isAccountFeaturesLoaded()
                .pipe(filter(Boolean))
                .subscribe(() => {
                    this.checkPaymentEnabled();
                    this.cdr.markForCheck();
                })
        );
        this.getPdfSecureUrl();
        this.subscriptions.push(
            this.porTransactionService.transactionSuccess$.subscribe(success => {
                if (success) {
                    this.isPending = true;
                    this.isPaymentEnabled = false;
                    this.refetchPaymentBoundaryAfterPayment();
                }
            })
        );
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 992px)']).subscribe((result: BreakpointState) => {
                this.isMobile = result.matches;
            })
        );

        if (this.versionToggle.isQuickLinkPdfExternalPortal()) {
            this.pdfDownloadAvailable$.next(false);
        }

        this.isProductDetailAvailable = this.featureToggleService.isAvailable('productDetailFeature');

        /**
         * Directly open payment secure form when input have special instruction
         * and other conditions met
         */
        this.subscriptions.push(
            this.isPaymentProcessorAvailable$.pipe(take(1)).subscribe(isAvailable => {
                if (this.openForPayment && this.isPaymentEnabled && isAvailable) {
                    this.enablePaymentPage(true);
                }
            })
        );
        if (this.contractDetail) {
            if (this.featureToggleService.isAvailable('canSeeContractDetailTotalPaid')) {
                this.appFacadeService.fetchContractDetailPaymentHistory(this.customerId, this.contractDetail?.[0].Id);
            }
            this.appFacadeService.fetchContractDetailLineItems(this.customerId, this.contractDetail?.[0].Id);
            // AC2: the payment boundary itself is loaded from ngOnChanges (which Angular guarantees runs
            // before ngOnInit on the initial binding) keyed by contract Id — see loadPaymentBoundaryFor().
        }
        this.subscriptions.push(
            this.appFacadeService.getConvertedContractIds().subscribe(ids => {
                this.convertedContractIds = ids;
                this.cdr.markForCheck();
            })
        );
        this.refreshEsignOnReturn();
        // AC1: expose the customer's Syrinx account-type status for the accordion visibility gate.
        this.subscriptions.push(
            this.appFacadeService.getCustomer().subscribe(customer => {
                this.customerStatus = customer?.Status;
            })
        );

        this.subscription.push(
            this.appFacadeService
                .isPaymentProcessorsAvailable()
                .pipe(
                    take(1), // only react once
                    filter(processors => processors === null)
                )
                .subscribe(() => {
                    this.appFacadeService.getPaymentProcessors(this.customerId);
                })
        );
    }

    get showStepTracker(): boolean {
        if (this.isStatus(ContractStatusEnum.Quote)) {
            return true;
        }
        return this.isStatus(ContractStatusEnum.Reservation) && this.autoConvertQuotes && this.convertedContractIds.includes(this.contractDetail?.[0]?.Id ?? '');
    }

    recomputeStepStatus(): void {
        if (this.isStatus(ContractStatusEnum.Reservation)) {
            // AC2: once converted, the tracker only ever shows the "Reservation complete" happy state.
            // Re-deriving payment/eSign completion from LIVE data here would be wrong: paymentMinimum is
            // GAPI's current collectible minimum (e.g. a balance due at pickup), not the deposit that was
            // already taken at conversion time - so a paid-off Reservation could otherwise show the
            // "cannot be processed until you complete the following steps" warning on a confirmed booking.
            this.quoteStepStatus = {
                payment: this.paymentRequired ? 'complete' : 'notRequired',
                eSign: this.eSignRequired ? 'complete' : 'notRequired',
                hasAnyRequiredStep: this.paymentRequired || this.eSignRequired,
                allRequiredStepsComplete: true,
                firstIncompleteRequiredStep: null,
                shouldAutoConvert: false
            };
            return;
        }
        this.quoteStepStatus = getQuoteStepStatus({
            paymentRequired: this.paymentRequired,
            eSignRequired: this.eSignRequired,
            paymentMinimum: this.paymentMinimum,
            eSignUrl: this.contractDetail?.[0]?.EsignUrl,
            eSignState: this.contractDetail?.[0]?.ESignState
        });
    }

    onGoToStep(step: QuoteStepName): void {
        if (step === 'payment') {
            // Same guard as the openForPayment auto-open in ngOnInit: don't open the payment page for a
            // config where the org has disabled it (QuotesPaymentAbility, SeePricing, licence, Voided) or
            // no processor matches the depot.
            this.isPaymentProcessorAvailable$.pipe(take(1)).subscribe(isAvailable => {
                if (this.isPaymentEnabled && isAvailable) {
                    this.enablePaymentPage(true);
                }
            });
        } else {
            this.startEsign();
        }
    }

    // AC2: load the payment boundary so the completion badge/step tracker have a minimum to compare
    // against. Called from ngOnChanges (initial load AND every later contract switch) rather than
    // ngOnInit, because this component is reused — not recreated — across contract switches on the
    // Contracts tab, so a one-time ngOnInit fetch would leave paymentMinimum (and therefore
    // quoteStepStatus) stuck on whichever contract was viewed first.
    private loadPaymentBoundaryFor(contractId: string): void {
        this.subscriptions.push(
            this.appMediatorService.porPayService
                .getMinMaxFromPaymentBoundary(this.customerId, contractId)
                // ADO-104813 Issue #2: resolve the minimum on error/no-emit so the completion badge never
                // stays 'pending' (endless "Loading…" in the Payment Information accordion). A missing or
                // empty boundary means the minimum is UNKNOWN — do NOT treat it as met (0)/'complete';
                // fall back to a positive minimum so the step reads 'incomplete'.
                .pipe(catchError(() => of(null)))
                .subscribe(boundaries => {
                    // Stale-response guard: if the consumer has already switched to a different contract
                    // while this request was in flight, this component instance is now showing that other
                    // contract — applying this response would overwrite its correct paymentMinimum with
                    // data for the contract that's no longer displayed.
                    if (this.contractDetail?.[0]?.Id !== contractId) {
                        return;
                    }
                    this.applyPaymentBoundaries(boundaries);
                })
        );
    }

    ngOnChanges(): void {
        // WHY: this component is reused (not recreated) across contract switches, so without this,
        // paymentMinimum from a previously-viewed contract would linger and quoteStepStatus would show
        // the new contract's Payment step using the old contract's data.
        // HOW: whenever the incoming contract's Id differs from the one paymentMinimum was last fetched
        // for, reset to 'pending' (null) and kick off a fresh fetch keyed to the new Id — loadPaymentBoundaryFor's
        // own stale-response guard makes sure a slow response for the OLD contract can't land after this
        // and overwrite the new one. recomputeStepStatus() then runs on every change regardless (including
        // eSign-only updates, e.g. after returning from signing) so the tracker always reflects current inputs.
        const currentContractId = this.contractDetail?.[0]?.Id;
        if (currentContractId && currentContractId !== this.paymentBoundaryContractId) {
            this.paymentBoundaryContractId = currentContractId;
            this.paymentMinimum = null;
            this.loadPaymentBoundaryFor(currentContractId);
        }
        this.recomputeStepStatus();
    }

    @HostListener('window:scroll', ['$event'])
    @HostListener('window:mousewheel', ['$event'])
    @HostListener('window:touchmove', ['$event'])
    scrollFunction(): void {
        const quickLinkContainer = document.querySelector('.quick-link-container');
        if (quickLinkContainer) {
            if (quickLinkContainer.scrollTop > 60) {
                this.isSticky = true;
            } else {
                this.isSticky = false;
            }
        }
    }

    ngOnDestroy(): void {
        /**
         * Remove contract id from state
         */
        if (this.contractDetail) {
            this.appFacadeService.unsetSelectedContracts([this.contractDetail?.[0]?.Id]);
        }
        this.contractDetail = null;
        this.enablePaymentPage(false);
        this.subscriptions.map((sub: Subscription) => sub.unsubscribe());
        // Set the gloabl transaction success to false after component destroy
        this.porTransactionService.setTransactionSuccess(false);
    }

    handleLoadMore(count: number) {
        this.displayedRowCount += count;
    }

    enablePaymentPage(action: boolean) {
        this.loginButtonVisibilityChange.emit(!action);
        if (this.featureToggleService.isAvailable('capturePaymentFeature')) {
            this.appFacadeService.setPaymentSectionEnabled(action);
            return;
        }
        if (action) {
            this.appEventService.dispatch({
                actionName: OriginActionName.CapturePayment,
                data: {
                    contractId: this.contractDetail?.[0]?.Id
                }
            });
        }
    }

    onSuccessTransaction(paymentResponse: Payment): void {
        if (paymentResponse) {
            this.contractDetail?.[0]?.Payment?.push(paymentResponse);
        }
    }

    getPdfSecureUrl(): void {
        // ADO-107388: mirror the two document lookups the API actually has, rather than
        // enumerating org types. CustomerService.getContractPdf branches once — Syrinx goes to
        // /storedDocuments/lookup by DocumentField/DocumentValue, everything else goes to
        // /storedfiles filtered on `Tags.TagName IN [...]`, which is org-type agnostic. Listing
        // types individually meant any type that wasn't 'expert' or 'syrinx' (notably 'essentials',
        // which is what CloudRent sends for a Unified Storefront session) fell through with an
        // empty value and requested `DocumentField=`. That returns HTTP 200 with no document, so
        // the PDF button was suppressed with nothing logged. A default cannot be empty here.
        // 'pdf,pdf_contract' matches both tags to support the legacy pdf_contract value.
        const documentFieldValue: string = this.featureToggleService.isSyrinx() ? 'Contract' : 'pdf,pdf_contract';
        if (!(this.featureToggleService.isSyrinx() && this.versionToggle.isQuickLinkVersion())) {
            if (this.pdfDownloadAvailable$.value) {
                this.subscription.push(
                    this.appFacadeService.pdf
                        .downloadPdf(this.customerId, this.contractDetail?.[0]?.Id, documentFieldValue)
                        .pipe(
                            tap(data => {
                                if (data && (data?.SecureURL || data?.URL)) {
                                    return this.pdfDownloadUrl$.next(data);
                                }
                                this.pdfDownloadAvailable$.next(false);
                                return of(null);
                            }),
                            catchError(() => {
                                this.pdfDownloadAvailable$.next(false);
                                this.pdfDownloadUrl$.next(null); // Set PDF URL to null on error
                                return of(null); // Return null to complete the observable chain
                            })
                        )
                        .subscribe()
                );
            }
        }
    }

    tryOpenSecureUrl(secureUrl: string | undefined, linkField: LinkField): boolean {
        if (this.versionToggle.isQLButNotConsumerQL()) {
            /**
             * Organization has !SecureUrlLoginRequired, which means that real URL is returned and We want to open it in new tab
             */
            if (secureUrl && secureUrl !== 'fakeUrl') {
                this.appFacadeService.pdf.openPdfinNewTab(secureUrl);
                return true;
            }

            const user: ConsumerPortalConfig = this.appMediatorService.localStorageService.getCurrentUser as ConsumerPortalConfig;

            this.appMediatorService.localStorageService.persistUser(AuthKeys.User, {
                ...user,
                operations: {
                    contract: {
                        [linkField]: secureUrl
                    }
                }
            });
            this.appFacadeService.updateLocation(ComponentsToLoad.Logout);
            this.returnToLogin.emit(true);
            return true;
        }
        return false;
    }

    downloadPdf(): void {
        // For QL open or redirect to the contract pdf url
        this.subscriptions.push(
            this.pdfDownloadUrl$.subscribe(data => {
                if (data) {
                    const secureUrl = data?.SecureURL || data?.URL;
                    if (secureUrl && !this.tryOpenSecureUrl(secureUrl, LinkField.ContractPdfUrl)) {
                        this.appFacadeService.pdf.openPdfinNewTab(secureUrl);
                    }
                }
            })
        );
    }

    loadProductDetailModal(productId: string | undefined, stockId: string | undefined, documentValue?: string): void {
        if (productId || stockId) {
            this.appFacadeService.loadProductDetail(
                this.customerId,
                this.contractDetail?.[0].Id ?? '',
                productId ?? '0',
                stockId ?? '0',
                ['Id', 'Name', 'SerialNumber', 'Images', 'Description'],
                () => {
                    this.setActiveTemplate(ContractDetailView.ProductDetail);
                },
                documentValue ?? ''
            );
        }
    }

    loadDocuments(item: LineItems): void {
        this.loadProductDetailModal(item?.ProductId, item?.StockId, item?.Id);
        this.contractId = item?.AdditionalFields?.ContractId;
    }

    editContract(contractId: string) {
        this.appEventService.dispatch({
            actionName: OriginActionName.ContractEdit,
            data: {
                contractId: contractId
            }
        });
    }

    toggleDialog() {
        this.isDialogEnabled = true;
    }

    cancelContract(confirmation: boolean, contractId: string) {
        if (!confirmation) {
            this.isDialogEnabled = false;
            return;
        }

        this.appEventService.dispatch({
            actionName: OriginActionName.ContractCancel,
            data: {
                contractId: contractId
            }
        });
        this.reloadContractList.emit();
    }

    openContractCloneModal() {
        this.modal.open('clone-contract-request-modal');
        return;
    }

    onCloseContractCloneModal() {
        this.requestActionService.setActiveTab(RequestActionsToLoad.DefaultView);
    }

    closeContractCloneModal() {
        this.modal.close('clone-contract-request-modal');
        this.reloadContractList.emit();
    }

    openPaymentHistoryModal(): void {
        this.modal.open('contract-payment-history-modal');
        return;
    }

    hasPaymentHistory(): boolean {
        if (!this.contractDetail?.[0]?.Payment || !this.versionToggle.isAdvanceVersion()) {
            return false;
        }
        return this.contractDetail[0].Payment.length > 0;
    }

    onClosePaymentHistoryModal() {
        this.requestActionService.setActiveTab(RequestActionsToLoad.DefaultView);
    }

    onCloseButtonClick() {
        if (this.isPending !== true) {
            this.enablePaymentPage(false);
            this.closePanel.emit(true);
        } else {
            this.closePanel.emit(true);
            this.reloadContractList.emit();
        }
    }

    onCloseProductDetail() {
        this.setActiveTemplateTab('DefaultView');
        this.appFacadeService.resetProductDetail();
    }

    hasPickupDate() {
        if (!this.hasContractDetail()) {
            return;
        }

        const pickupDateString: string = this.contractDetail?.[0]?.DriverPickup?.DateTime as string;
        const deliveryDateString: string = this.contractDetail?.[0]?.DriverDelivery?.DateTime as string;

        if (!pickupDateString || !deliveryDateString) {
            return;
        }

        const pickupDate: Date = new Date(pickupDateString);
        const deliveryDate: Date = new Date(deliveryDateString);

        return pickupDate >= deliveryDate;
    }

    loadItemActions(lineItem: LineItems): void {
        this.selectedLineItem = lineItem;
        this.setActiveTemplate(ContractDetailView.SelectedItemActions);
    }

    private setActiveTemplate(tab: ContractDetailView): void {
        this.activeTemplate.next(tab);
    }

    setActiveTemplateTab(tab: string): void {
        // Convert the string to the corresponding enum value
        const enumValue: ContractDetailView = ContractDetailView[tab as keyof typeof ContractDetailView];
        // Call setActiveTemplate with the enum value
        this.setActiveTemplate(enumValue);
    }

    getContractTotal(): number | string {
        if (!this.hasContractDetail()) {
            return '';
        }

        const grandTotal: string | undefined = this.contractDetail?.[0]?.GrandTotal;
        const grandTaxTotal: string | undefined = this.contractDetail?.[0]?.GrandTaxTotal;

        if (typeof grandTotal !== 'number' || typeof grandTaxTotal !== 'number') {
            return '';
        }

        const result: number = grandTotal - grandTaxTotal;
        return result;
    }

    getDamageWaiver(): number {
        return this.contractDetail?.[0]?.Totals?.find(t => t.Label === 'Damage Waiver')?.Value ?? 0;
    }

    isSaleorRentalorNull(classification: string): boolean {
        return classification === LineItemClassification.RENTAL || classification === LineItemClassification.SALE || classification === null;
    }

    isRental(classification: string): boolean {
        return classification === LineItemClassification.RENTAL;
    }

    isShowLink(classification: string): boolean {
        return classification === LineItemClassification.RENTAL && this.featureToggleService.isAvailable('showDetailDocuments');
    }

    isEsignAvailable(): boolean {
        if (!this.hasContractDetail()) {
            return false;
        }

        // Check if EsignUrl is not null, not empty, and eSignState is "Requested" if it exists
        if (this.contractDetail && this.contractDetail[0] && (this.contractDetail[0].EsignUrl || false) && (!this.contractDetail[0].ESignState || this.contractDetail[0].ESignState === 'Requested')) {
            return true;
        }
        return false;
    }

    hasContractDetail(): boolean {
        if (this.contractDetail?.[0] || this.contractDetail?.length) {
            return true;
        }
        return false; // Return false if contractDetail is null or undefined
    }

    /**
     * Check the contract status is respected one or not
     * @param status
     * @returns boolean
     */
    isStatus(status: ContractStatus): boolean {
        // Get the contract status, considering both possible properties
        const contractStatus: string | undefined = this.contractDetail?.[0]?.Status ?? this.contractDetail?.[0].ContractStatus;
        switch (contractStatus && status) {
            case ContractStatusEnum.Quote: {
                return contractStatus === ContractStatusEnum.Quote;
            }
            case ContractStatusEnum.Open: {
                return contractStatus === ContractStatusEnum.Open;
            }
            case ContractStatusEnum.Closed: {
                return contractStatus === ContractStatusEnum.Closed;
            }
            case ContractStatusEnum.Reservation: {
                return contractStatus === ContractStatusEnum.Reservation;
            }
            case ContractStatusEnum.Returned: {
                return contractStatus === ContractStatusEnum.Returned;
            }
            case ContractStatusEnum.Voided: {
                return contractStatus === ContractStatusEnum.Voided;
            }
            default:
                return false;
        }
    }

    hideActionButtons(): boolean {
        if ((this.contractDetail?.[0]?.AmountDue != null && this.contractDetail?.[0].AmountDue > 0 && !this.isPending) || this.checkEsignButton()) {
            return true;
        } else {
            return false;
        }
    }

    checkEsignButton(): boolean {
        if (this.versionToggle.isQuickLinkVersion() && this.isEsignAvailable()) {
            return true;
        } else {
            return false;
        }
    }

    // ADO-120176: shows the incomplete-steps banner on a QuickLink Quote whenever eSign is
    // outstanding or a required payment is still incomplete ('pending' = still loading, so not yet).
    showIncompleteStepsBanner(): boolean {
        if (!this.versionToggle.isQuickLinkVersion() || !this.isStatus(ContractStatusEnum.Quote) || !this.featureToggleService.isAvailable('completeReservationButton')) {
            return false;
        }
        const paymentIncomplete = this.isPaymentAccordionVisible && this.paymentRequired && this.getPaymentStepStatus(this.contractDetail?.[0]) === 'incomplete';
        return this.checkEsignButton() || paymentIncomplete;
    }

    isNoterLineItem(item: LineItems): boolean {
        return item?.isNoter ? true : false;
    }

    isAvoidLineItem(item: LineItems) {
        return AVOID_LINE_ITEM_NAMES.some(keyword => item.Name.toLocaleLowerCase().includes(keyword)) ? false : true;
    }

    showCloneContractButton(): boolean {
        if (!this.hasContractDetail()) {
            return false;
        }
        const lineItems = this.contractDetail?.[0]?.LineItems ?? [];
        return lineItems.some(item => !AVOID_LINE_ITEM_NAMES.some(keyword => item?.Name?.toLowerCase().includes(keyword)));
    }
}
